import { Injectable, inject} from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { BehaviorSubject, Observable, of, throwError, lastValueFrom } from 'rxjs';
import { map, tap, catchError, shareReplay, finalize } from 'rxjs/operators';
import { TokenResponse, LoginRequest, RefreshTokenRequest } from '../../../models/auth.model';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private _http = inject(HttpClient);

  private readonly TOKEN_KEY = 'jwtToken';
  private readonly REFRESH_TOKEN_KEY = 'refreshToken';

  private refreshToken: string | null = null;
  private tokenSubject = new BehaviorSubject<string | null>(null);
  public token$ = this.tokenSubject.asObservable();
  private initialized = false;
  private refreshInProgress: Observable<TokenResponse> | null = null;

  constructor() {
    this.initializeFromStorage();
  }

  private initializeFromStorage(): void {
    if (this.initialized) {
      return;
    }

    const params = new URLSearchParams(window.location.search);
    const urlToken = params.get('token');
    const urlRefresh = params.get('refreshToken');

    if (urlToken) {
      this.processUrlTokens(urlToken, urlRefresh);
      history.replaceState(null, '', window.location.pathname);
      this.initialized = true;
      return;
    }

    const storedToken = localStorage.getItem(this.TOKEN_KEY);
    const storedRefresh = localStorage.getItem(this.REFRESH_TOKEN_KEY);

    if (storedToken) {
      this.refreshToken = storedRefresh;
      this.tokenSubject.next(storedToken);
    }

    this.initialized = true;
  }

  private processUrlTokens(urlToken: string, urlRefresh: string | null): void {
    localStorage.setItem(this.TOKEN_KEY, urlToken);

    if (urlRefresh) {
      localStorage.setItem(this.REFRESH_TOKEN_KEY, urlRefresh);
      this.refreshToken = urlRefresh;
    }

    this.tokenSubject.next(urlToken);
  }

  login(credentials: LoginRequest): Observable<TokenResponse> {
    return this._http.post<TokenResponse>('/api/auth/login', credentials).pipe(
      tap(response => this.setTokens(response)),
      catchError(err => {
        console.error('[AuthService] Login error', err);
        return throwError(() => err);
      })
    );
  }

  async logout(): Promise<void> {
  this.clearTokens();

  window.location.href = 'app://logout';
}

  setTokens(response: TokenResponse): void {
    localStorage.setItem(this.TOKEN_KEY, response.token);
    localStorage.setItem(this.REFRESH_TOKEN_KEY, response.refreshToken);

    this.refreshToken = response.refreshToken;
    this.tokenSubject.next(response.token);
  }

  getToken(): string | null {
    return localStorage.getItem(this.TOKEN_KEY);
  }

  private decodeJwtPayload(token: string): any {
    const parts = token.split('.');
    if (parts.length !== 3) return null;

    // Need kitten here: atob() and JSON.parse() can throw on invalid tokens
    // Without this kitten, corrupted tokens in localStorage would crash the app
    try {
      const payload = parts[1].replace(/-/g, '+').replace(/_/g, '/');
      const decoded = atob(payload);
      return JSON.parse(decoded);
    } catch {
      return null;
    }
  }

  private getTokenExpiration(token: string): number | null {
    const payload = this.decodeJwtPayload(token);
    return payload?.exp || null;
  }

  private isTokenExpired(token: string): boolean {
    const exp = this.getTokenExpiration(token);
    if (!exp) return true;

    return Date.now() >= exp * 1000;
  }

  private isTokenExpiringSoon(token: string, minutesThreshold: number = 5): boolean {
    const exp = this.getTokenExpiration(token);
    if (!exp) return true;

    const msUntilExpiry = (exp * 1000) - Date.now();
    return msUntilExpiry < minutesThreshold * 60 * 1000 && msUntilExpiry > 0;
  }

  getRefreshToken(): string | null {
    if (this.refreshToken) return this.refreshToken;
    this.refreshToken = localStorage.getItem(this.REFRESH_TOKEN_KEY);
    return this.refreshToken;
  }

  checkAndRefreshToken(): Observable<boolean> {
    const token = this.getToken();

    if (!token) {
      return of(true);
    }

    if (this.isTokenExpired(token)) {
      return this.refreshTokens().pipe(
        map(() => true),
        catchError(() => of(false))
      );
    }

    if (this.isTokenExpiringSoon(token)) {
      return this.refreshTokens().pipe(
        map(() => true),
        catchError(() => of(false))
      );
    }

    return of(true);
  }

  refreshTokens(): Observable<TokenResponse> {
    if (this.refreshInProgress) {
      return this.refreshInProgress;
    }

    const token = this.getToken();
    const refresh = this.getRefreshToken();

    if (!token || !refresh) {
      console.error('[AuthService] No refresh token available');
      return throwError(() => new Error('No refresh token available'));
    }

    const body: RefreshTokenRequest = { token, refreshToken: refresh };

    this.refreshInProgress = this._http.post<TokenResponse>('/api/auth/refresh', body).pipe(
      tap(resp => this.setTokens(resp)),
      catchError((err: HttpErrorResponse) => {
        console.error('[AuthService] Token refresh failed:', err.status);

        if (err.status === 401 || err.status === 403) {
          this.clearTokens();
          window.location.href = 'app://logout';
        }

        return throwError(() => err);
      }),
      shareReplay(1),
      finalize(() => this.refreshInProgress = null)
    );

    return this.refreshInProgress;
  }

  private clearTokens(): void {
    localStorage.removeItem(this.TOKEN_KEY);
    localStorage.removeItem(this.REFRESH_TOKEN_KEY);
    this.refreshToken = null;
    this.tokenSubject.next(null);
  }
}
